/**
 * Before/After Slider Web Component
 * Creates an interactive before/after image comparison slider
 */
class BeforeAfterSlider extends HTMLElement {
  constructor() {
    super();
    this.sliderPosition = 50;
    this.isDragging = false;
  }

  static get observedAttributes() {
    return ['before-image', 'after-image', 'before-label', 'after-label', 'slider-position'];
  }

  connectedCallback() {
    this.sliderPosition = parseFloat(this.getAttribute('slider-position')) || 50;
    this.render();
    this.setupEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) {
      if (name === 'slider-position') {
        this.sliderPosition = parseFloat(newValue) || 50;
        this.updateSliderPosition();
      } else {
        this.render();
        this.setupEventListeners();
      }
    }
  }

  render() {
    const beforeImage = this.getAttribute('before-image') || '';
    const afterImage = this.getAttribute('after-image') || '';
    const beforeLabel = this.getAttribute('before-label') || 'Before';
    const afterLabel = this.getAttribute('after-label') || 'After';
    const position = this.sliderPosition;

    this.innerHTML = `
      <div class="before-after-container">
        <div class="before-after-wrapper">
          <div class="before-image-container">
            <img src="${beforeImage}" alt="${beforeLabel}" class="before-image" loading="lazy">
            <span class="before-label">${beforeLabel}</span>
          </div>
          <div class="after-image-container" style="clip-path: inset(0 ${100 - position}% 0 0);">
            <img src="${afterImage}" alt="${afterLabel}" class="after-image" loading="lazy">
            <span class="after-label">${afterLabel}</span>
          </div>
          <div class="slider-handle" style="left: ${position}%">
            <div class="slider-line"></div>
            <div class="slider-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 12L16 12M12 8L12 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    const container = this.querySelector('.before-after-wrapper');
    const handle = this.querySelector('.slider-handle');
    const afterContainer = this.querySelector('.after-image-container');

    if (!container || !handle || !afterContainer) return;

    // Mouse events
    container.addEventListener('mousedown', (e) => this.startDrag(e));
    document.addEventListener('mousemove', (e) => this.onDrag(e));
    document.addEventListener('mouseup', () => this.stopDrag());

    // Touch events
    container.addEventListener('touchstart', (e) => this.startDrag(e), { passive: false });
    document.addEventListener('touchmove', (e) => this.onDrag(e), { passive: false });
    document.addEventListener('touchend', () => this.stopDrag());

    // Click to move
    container.addEventListener('click', (e) => {
      if (!this.isDragging) {
        this.updatePosition(e);
      }
    });
  }

  startDrag(e) {
    this.isDragging = true;
    this.updatePosition(e);
    e.preventDefault();
  }

  onDrag(e) {
    if (!this.isDragging) return;
    this.updatePosition(e);
    e.preventDefault();
  }

  stopDrag() {
    this.isDragging = false;
  }

  updatePosition(e) {
    const container = this.querySelector('.before-after-wrapper');
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const position = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    this.sliderPosition = position;
    this.updateSliderPosition();
  }

  updateSliderPosition() {
    const handle = this.querySelector('.slider-handle');
    const afterContainer = this.querySelector('.after-image-container');

    if (handle) {
      handle.style.left = `${this.sliderPosition}%`;
    }
    if (afterContainer) {
      afterContainer.style.clipPath = `inset(0 ${100 - this.sliderPosition}% 0 0)`;
    }
  }
}

// Register the custom element
if (!customElements.get('before-after-slider')) {
  customElements.define('before-after-slider', BeforeAfterSlider);
}
